import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'football');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const categoryArticles = articles.filter((a) => {
    const catMap: Record<string, string> = {
      football: '足球',
      basketball: '篮球',
      esports: '电竞',
      tennis: '网球',
      racing: '赛车',
      boxing: '搏击',
    };
    return a.category === catMap[activeCategory] || a.category === '使用教程' || a.category === '投注指南';
  });

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '赛事分类', href: '/category/football' },
    { label: currentCategory.name },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 开云体育`,
    description: currentCategory.description,
    url: `https://www.kaiyunsports.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '开云体育官方下载',
      url: 'https://www.kaiyunsports.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 mx-auto mb-4">
                  <i className={`${currentCategory.icon} text-2xl w-8 h-8 flex items-center justify-center`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                  {currentCategory.name}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                  {currentCategory.description}
                </p>
                <p className="text-sm text-primary-600 font-medium mt-3">
                  当前共 {currentCategory.count.toLocaleString()}+ 场赛事可参与
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-2 mb-10">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-200/60 hover:text-primary-600'
                    }`}
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.length > 0 ? (
                  categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group block rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/60 bg-background-50 transition-all duration-300 no-underline"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3 text-xs text-foreground-400 flex-wrap">
                          <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 font-medium">
                            {article.category}
                          </span>
                          <span>{article.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
                            {article.readTime}
                          </span>
                        </div>
                        <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="col-span-full text-center py-16">
                    <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mx-auto mb-4">
                      <i className="ri-inbox-line text-2xl w-8 h-8 flex items-center justify-center"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无相关文章</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}